/**
 * Agents hub card copy — category / tagline / description per agent type.
 *
 * The registry (central-api `clientagents.agent_types`) is the source of truth
 * and ships these on `TypeView`. This map is the FALLBACK for an API that
 * predates the fields (the hub must never render an empty eyebrow or a blank
 * card), keyed by `agent_type`. Unknown types degrade to a generic category
 * and the type's label.
 *
 * The copy is English. A translator, when given, may replace the copy of the
 * built-in types (keys `AgentTypeTagline_<agent_type>`,
 * `AgentTypeDescription_<agent_type>`) and the category names
 * (`AgentCategory_<Category>`). A language whose value for such a key is empty
 * (English) or missing keeps the registry's own text, so the registry stays
 * the source of the English copy. Agents an agency built itself are never
 * translated.
 */
import type { TypeView } from '../api/client-reports/client-agents-api';

export interface AgentTypeMeta {
  category: string;
  tagline: string;
  description: string;
}

const FALLBACK: Record<string, AgentTypeMeta> = {
  client_reporting: {
    category: 'Reporting',
    tagline: 'Drafts your full client report, every period.',
    description:
      "Pulls every connected channel, scores results against the brand's KPI targets, writes the narrative and builds the deck. Your team edits the wording in place and approves — then it publishes to the white-label client portal.",
  },
  google_sheets: {
    category: 'Analysis',
    tagline: 'Builds the KPI workbook, every period.',
    description:
      "Pulls period performance from every connected platform, compares it with the previous period and the brand's KPI targets, and builds an Excel workbook with real formulas — a summary sheet plus one sheet per platform.",
  },
  kpi_check: {
    category: 'Analysis',
    tagline: 'Scores this brand against its targets, every Monday.',
    description:
      "Compares last week's results with the KPI targets you set for this brand — spend, ROAS, CPA, revenue and every other target — per platform and blended. Flags what's on track, what's drifting and what needs a decision, before your Monday stand-up.",
  },
  anomaly_alert: {
    category: 'Analysis',
    tagline: 'Flags what went wrong yesterday, before the client notices.',
    description:
      "Every morning it compares yesterday's spend, delivery, efficiency and conversions per account and per campaign with the last 14 days and the same weekday in earlier weeks. It filters out noise and only speaks up when something needs action: a campaign that stopped delivering, a budget that ran away, a CPA that doubled, conversions that fell to zero. A quiet day is one line.",
  },
  assistant: {
    category: 'Chat',
    tagline: "Ask anything about this brand's numbers.",
    description:
      'An interactive assistant scoped to one brand. It reads the connected platforms through the same governed tools, remembers context per brand, and can propose a report run for you to confirm.',
  },
};

/** Shaped like react-i18next's `t(key, defaultValue, options)`. */
type Translate = (
  key: string,
  defaultValue: string,
  options?: Record<string, unknown>
) => string;

/** No translator given: the English text with its `{{placeholders}}` filled in. */
const untranslated: Translate = (_key, defaultValue, options) =>
  defaultValue.replace(/\{\{\s*(\w+)\s*\}\}/g, (_m, name) =>
    String(options?.[name] ?? '')
  );

const nonEmpty = (v: unknown): string =>
  typeof v === 'string' && v.trim() !== '' ? v.trim() : '';

/** Registry copy first, fallback map second, generic last. */
export function agentTypeMeta(
  type: Pick<TypeView, 'agent_type' | 'label'> &
    Partial<Pick<TypeView, 'category' | 'tagline' | 'description'>>,
  t: Translate = untranslated
): AgentTypeMeta {
  const fb = FALLBACK[type.agent_type];
  const category = nonEmpty(type.category) || fb?.category || 'Agent';
  const tagline = nonEmpty(type.tagline) || fb?.tagline || '';
  const description = nonEmpty(type.description) || fb?.description || '';
  /** The language's own text for `key`, or the registry's when it has none. */
  const override = (key: string, value: string): string =>
    nonEmpty(t(key, '')) || value;
  return {
    category: override(
      `AgentCategory_${category.replace(/\W+/g, '')}`,
      category
    ),
    tagline: tagline
      ? fb
        ? override(`AgentTypeTagline_${type.agent_type}`, tagline)
        : tagline
      : t(
          'AgentTypeDefaultTagline',
          '{{agent}} runs for this brand on a schedule.',
          { agent: type.label }
        ),
    description:
      fb && description
        ? override(`AgentTypeDescription_${type.agent_type}`, description)
        : description,
  };
}
